import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, CheckCircle, Check } from 'lucide-react';

interface PaymentTerm {
  id: string;
  label: string;
  label_en: string;
  description?: string;
}

interface PaymentTermsBlockProps {
  data: unknown;
  onUpdateFields?: (fields: Record<string, unknown>) => void;
}

const PaymentTermsBlock: React.FC<PaymentTermsBlockProps> = ({ data, onUpdateFields }) => {
  const { i18n } = useTranslation();
  const isZh = i18n.language?.startsWith('zh');
  const parsed = data as { terms: PaymentTerm[]; default_id?: string } | undefined;
  const terms = parsed?.terms ?? [];
  const defaultId = parsed?.default_id;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  if (!terms.length) return null;

  const handleSelect = (term: PaymentTerm) => {
    setSelectedId(term.id);
    onUpdateFields?.({ payment_terms: term.label });
  };

  // Collapsed after selection
  if (selectedId) {
    const chosen = terms.find(t => t.id === selectedId);
    return (
      <div className="mt-2 rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-2 flex items-center gap-2 text-xs">
        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">
          {isZh ? '付款條件' : 'Payment terms'}: {isZh ? chosen?.label : chosen?.label_en}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-3 py-2 bg-blue-50 dark:bg-blue-900/20 flex items-center gap-2 text-xs font-medium text-blue-700 dark:text-blue-400">
        <CreditCard className="w-3.5 h-3.5" />
        {isZh ? '選擇付款條件' : 'Select payment terms'}
      </div>

      {/* Term list */}
      <div className="p-2 space-y-1">
        {terms.map(term => {
          const isDefault = term.id === defaultId;
          return (
            <button
              key={term.id}
              onClick={() => handleSelect(term)}
              className="w-full text-left px-3 py-2 rounded-md border border-gray-100 dark:border-gray-600 hover:border-blue-300 hover:bg-blue-50/50 dark:hover:border-blue-700 dark:hover:bg-blue-900/10 transition-colors flex items-center gap-2 group"
            >
              <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-500 group-hover:border-blue-500 flex items-center justify-center flex-shrink-0">
                {isDefault && <Check className="w-3 h-3 text-blue-500" />}
              </div>
              <div className="flex-1">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  {isZh ? term.label : term.label_en}
                </span>
                {term.description && (
                  <span className="text-[10px] text-gray-400 ml-2">{term.description}</span>
                )}
              </div>
              {isDefault && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400">
                  {isZh ? '預設' : 'Default'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentTermsBlock;
